import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

interface Hit {
  plate: string;
  brand: string | null;
  vehicleTypeName: string | null;
  isTrailer: boolean;
  trailerPlate: string | null;
}

const normalize = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, "");

/**
 * Campo de placa que sugere do cadastro enquanto digita — o mesmo comportamento
 * do "Buscar placa" da Visão Geral (debounce, setas, Enter, Esc), mas como
 * FILTRO: escolher a placa chama `onSelect`, e o × limpa.
 *
 * Sugere só a frota própria ATIVA (`/maintenance/vehicles`), a mesma frota que o
 * plano, o painel e os grupos consideram — placa de terceiro ou vendida não aparece.
 */
export function PlateAutocomplete({ value, onSelect, placeholder = "Placa", className = "w-44" }: {
  value: string;
  onSelect: (plate: string) => void;
  placeholder?: string;
  className?: string;
}) {
  const [texto, setTexto] = useState(value);
  const [aberto, setAberto] = useState(false);
  const [ativo, setAtivo] = useState(0);
  const [debounced, setDebounced] = useState("");
  const raiz = useRef<HTMLDivElement>(null);

  useEffect(() => { setTexto(value); }, [value]);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(normalize(texto)), 180);
    return () => clearTimeout(t);
  }, [texto]);
  useEffect(() => {
    const fora = (e: MouseEvent) => {
      if (raiz.current && !raiz.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);

  const buscar = aberto && debounced.length >= 2 && debounced !== value;
  const { data, isFetching } = useQuery<{ items: Hit[] }>({
    queryKey: ["maintenance", "vehicles", "autocomplete", debounced],
    queryFn: () => api.get(`/maintenance/vehicles?q=${encodeURIComponent(debounced)}`),
    enabled: buscar,
    staleTime: 60_000,
  });
  const hits = buscar ? (data?.items ?? []) : [];

  const escolher = (plate: string) => {
    const p = normalize(plate);
    setAberto(false);
    setTexto(p);
    onSelect(p);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setAtivo((i) => Math.min(i + 1, Math.max(hits.length - 1, 0))); setAberto(true); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setAtivo((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      // Só filtra por placa que existe na frota: Enter escolhe a sugestão, nunca o texto solto.
      if (hits[ativo]) escolher(hits[ativo].plate);
    }
    else if (e.key === "Escape") setAberto(false);
  };

  return (
    <div ref={raiz} className={`relative ${className}`}>
      <input
        type="text"
        value={texto}
        onChange={(e) => { setTexto(e.target.value.toUpperCase()); setAtivo(0); setAberto(true); }}
        onFocus={() => setAberto(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        aria-label="Filtrar por placa"
        autoComplete="off"
        spellCheck={false}
        className="input w-full uppercase font-mono pr-7"
      />
      {(value || texto) && (
        <button
          type="button"
          aria-label="Limpar placa"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
          onClick={() => { setTexto(""); setAberto(false); onSelect(""); }}
        >
          ×
        </button>
      )}
      {buscar && (
        <div className="absolute left-0 right-0 mt-1 z-30 min-w-[16rem] rounded-lg bg-white shadow-lg border border-gray-200 overflow-hidden">
          {hits.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-500">
              {isFetching ? "Buscando…" : `Nenhuma placa da frota com "${debounced}".`}
            </div>
          ) : (
            <ul className="max-h-72 overflow-y-auto">
              {hits.map((h, i) => (
                <li key={h.plate}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => escolher(h.plate)}
                    onMouseEnter={() => setAtivo(i)}
                    className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 ${i === ativo ? "bg-tm-50" : "bg-white"}`}
                  >
                    <span className="text-sm font-mono font-medium text-gray-900">{h.plate}</span>
                    <span className="text-xs text-gray-500 truncate">
                      {h.isTrailer ? "carreta" : (h.vehicleTypeName ?? h.brand ?? "")}
                      {!h.isTrailer && h.trailerPlate ? ` · ${h.trailerPlate}` : ""}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
